/* eslint-disable security/detect-object-injection */
import {useState, useEffect} from 'react';
import _ from 'lodash';
import {Link, useParams, useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';
import {Helmet} from 'react-helmet';
import {SearchIcon} from '@heroicons/react/solid';
import {useMutation, useQuery, useQueryClient} from 'react-query';
import {
  HiOutlinePlus,
  HiOutlineSearch,
  HiOutlineTerminal,
  HiOutlineTrash,
} from 'react-icons/hi';
import {AxiosError} from 'axios';

import {trackEvent} from '../../../utils/tracking';
import {
  archiveDataset,
  Dataset,
  fetchAllDatasets,
  responseToDataset,
  editDataset,
  deleteDataset,
  getDatasetsByProject,
  ProjectWithDatasets,
} from '../../../models/dataset';
import {deleteProject, DeleteProjectRequest} from '../../../models/project';
import {ToastMessage} from '../../../core/components/toast';
import {Select, SelectOption} from '../../../core/components/select';
import {Modal} from '../../../core/layout/modal';
import {Loading} from '../../../core/components/loading';
import {CreateDatasetForm} from '../../../components/create-dataset-form';
import {DatasetCard} from '../../../components/dataset';
import {ApplicationShell} from '../../../core/layout/application-shell';
import {EmbedVideo} from '../../../core/components/embed-video';
import {DatasetStatusEnum} from 'src/enums/datasetstatusenum';
import {GroupedStatuses} from 'src/enums/datasetstatusgroupedenum';
import {useProjectContext} from '../../../models/project-context';
import {useAxios} from 'src/utils/http';
import {Breadcrumbs} from '../../../core/components/breadcrumbs';
import {useAuth} from '../../../hooks/auth';
import {isInternal} from '../../../models/auth';

export const DatasetList = () => {
  const {project_id} = useParams<{project_id: string}>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const api = useAxios();
  const {projectId, setProjectId} = useProjectContext();
  const {authState} = useAuth();
  const isInternalUser = isInternal(authState.profile?.email || '');
  const [modalType, modalTypeChange] = useState<
    'create' | 'edit' | 'request' | 'requestConfirmed' | 'delete' | 'archive'
  >();
  const [loading, setLoading] = useState(false);
  const [modalOpen, modalOpenChange] = useState(false);
  const [modalDataset, modalDatasetChange] = useState<Dataset>();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] =
    useState<keyof typeof GroupedStatuses>('All');
  const [confirmDeleteProject, setConfirmDeleteProject] = useState(false);

  // Fetch all projects with datasets if we have a project_id
  const {data: projectDataByOwner, isLoading: isProjectsLoading} = useQuery(
    ['projectsByOwner', authState.profile?.userId],
    () => getDatasetsByProject(api),
    {
      keepPreviousData: true,
      staleTime: 0,
      refetchOnMount: 'always',
      enabled: !!project_id, // Only fetch if we have a project_id
    }
  );

  // Find the current project
  const project: ProjectWithDatasets | undefined = project_id
    ? projectDataByOwner?.find(p => p.project_id === project_id)
    : undefined;

  // Filter datasets to only show those owned by current user and part of the project
  const projectNormalizedDatasets =
    project_id && project
      ? project.datasets.filter(d => d.userId === authState.profile?.userId)
      : [];

  // Convert NormalizedDataset to Dataset for project datasets
  const projectDatasets = projectNormalizedDatasets.map(normalized => ({
    id: normalized.id,
    userId: normalized.userId,
    name: normalized.name,
    status: normalized.status,
    studyCount: normalized.studyCount,
    includeUndetermined: normalized.includeUndetermined,
    favorite: normalized.favorite,
    dicomViewingProcessing: normalized.dicomViewingProcessing,
    projectId: normalized.projectId || '',
  }));

  // If we have a project_id, use project datasets, otherwise fetch all datasets
  const {data: allDatasets, isLoading: allDatasetsLoading} = useQuery(
    ['dataset'],
    () => fetchAllDatasets(api),
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000, // 5 minutes
      enabled: !project_id, // Only fetch if we don't have a project_id
    }
  );

  // Use project datasets if we have a project_id, otherwise use all datasets
  const datasets = project_id ? projectDatasets : allDatasets || [];
  const datasetsLoading = project_id ? isProjectsLoading : allDatasetsLoading;

  const isLocked = project?.project_state === 'closed';
  const isProjectOwner =
    project?.project_owner_id === authState.profile?.userId;

  // Set project context when component mounts
  useEffect(() => {
    if (project_id && project_id !== projectId) {
      setProjectId(project_id);
    }
  }, [project_id, setProjectId]);

  const editDatasetMut = useMutation(
    ({datasetId, name}: {datasetId: number; name: string}) =>
      editDataset(api, datasetId, name),
    {
      onSuccess: async response => {
        // Success
        const {data} = response;

        const dataset = responseToDataset(data);

        if (project_id) {
          // Invalidate projects query to refresh project datasets
          await queryClient.invalidateQueries(['projectsByOwner']);
        } else {
          // Update currently loaded dataset
          const index = _.findIndex(datasets, {id: dataset.id});
          const newDatasets = _.cloneDeep(datasets);
          newDatasets![index].name = data['name'];
          queryClient.setQueryData(['dataset'], newDatasets);
        }

        toast(<ToastMessage title="Dataset updated" icon="success" />);

        closeModal();
      },
      onError: (err: AxiosError) => {
        const message =
          (err?.response?.data as any)?.message ?? 'Error updating dataset';
        toast(<ToastMessage title={message} icon="error" />);
      },
    }
  );

  const deleteDatasetMut = useMutation(
    ({datasetId}: {datasetId: number}) => deleteDataset(api, datasetId),
    {
      onSuccess: async (response, {datasetId}) => {
        // Success
        toast(<ToastMessage title="Dataset deleted" icon="success" />);

        if (project_id) {
          // Invalidate projects query to refresh project datasets
          await queryClient.invalidateQueries(['projectsByOwner']);
        } else {
          // Update currently loaded dataset
          const newDatasets = _.filter(
            datasets,
            dataset => dataset.id !== datasetId
          );
          queryClient.setQueryData(['dataset'], newDatasets);
        }

        closeModal();
      },
      onError: (err: AxiosError) => {
        const message =
          (err?.response?.data as any)?.message ?? 'Error deleting dataset';
        toast(<ToastMessage title={message} icon="error" />);
      },
    }
  );

  const archiveDatasetMut = useMutation(
    ({datasetId}: {datasetId: number}) => archiveDataset(api, datasetId),
    {
      onSuccess: async datasetId => {
        if (project_id) {
          // Invalidate projects query to refresh project datasets
          await queryClient.invalidateQueries(['projectsByOwner']);
        } else {
          // Update currently loaded dataset
          const newDatasets = _.filter(
            datasets,
            dataset => dataset.id !== datasetId
          );
          queryClient.setQueryData(['dataset'], newDatasets);
        }
        closeModal();
      },
    }
  );

  const deleteProjectMutation = useMutation({
    mutationFn: (request: DeleteProjectRequest) => deleteProject(api, request),
    onSuccess: async () => {
      trackEvent('CLICK_DELETE_PROJECT', {
        project_id: project_id,
      });
      await queryClient.invalidateQueries(['projectsByOwner']);
      toast(<ToastMessage title="Project deleted" icon="success" />);
      navigate('/project');
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{message?: string}>;
      const msg =
        err.response?.data?.message ?? 'Something went wrong, try again later';
      toast.error(msg);
    },
  });

  const handleDeleteProject = () => {
    if (project_id && authState.profile?.userId) {
      deleteProjectMutation.mutate({
        project_id: project_id,
        user_id: authState.profile.userId,
      });
    }
  };

  // @todo: Create component for each modal
  const openModal = (
    type:
      | 'create'
      | 'edit'
      | 'request'
      | 'requestConfirmed'
      | 'delete'
      | 'archive',
    dataset?: Dataset
  ) => {
    // @todo: Don't use empty dataset for create modal
    const emptyDataset: Dataset = {
      id: 0,
      userId: 0,
      name: '',
      status: DatasetStatusEnum.DatasetStatusDatasetCreated,
      includeUndetermined: true,
      favorite: true,
      dicomViewingProcessing: false,
      projectId: project_id || '',
    };

    modalTypeChange(type);
    modalDatasetChange(_.cloneDeep(dataset || emptyDataset));
    modalOpenChange(true);
  };

  const closeModal = () => {
    modalOpenChange(false);
    modalTypeChange(undefined);
    modalDatasetChange(undefined);
  };

  const onSearchClick = () => {
    if (project_id) {
      navigate(`/project/${project_id}/search`);
    } else {
      navigate('/project');
    }
  };

  const onTerminalSearch = () => {
    if (project_id) {
      const basePath = `/project/${project_id}/terminal`;
      navigate(basePath);
    } else {
      navigate('/project');
    }
  };

  const renderDatasets = () => {
    if (datasetsLoading) {
      return <Loading text="Loading datasets..." />;
    }
    if (datasets?.length === 0) {
      if (project_id && isProjectOwner && !isLocked) {
        return (
          <div className="flex flex-col items-center py-12">
            <div className="text-center mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No datasets yet
              </h3>
              <p className="text-base text-gray-500 max-w-md">
                Create a dataset to start organizing your medical imaging data,
                or delete this project if you no longer need it.
              </p>
            </div>
            <div className="flex items-center gap-4">
              <button
                className="btn btn-primary py-2 px-6 flex items-center gap-2"
                onClick={() => {
                  trackEvent('CLICK_CREATE_DATASET_BTN', {
                    project_id: project_id,
                  });
                  setProjectId(project_id);
                  openModal('create');
                }}
                data-cy="emptyState_createDatasetButton"
              >
                <HiOutlinePlus className="w-5 h-5" />
                <span>Create Dataset</span>
              </button>
              <button
                className="btn btn-outline-danger py-2 px-6 flex items-center gap-2"
                onClick={() => {
                  trackEvent('CLICK_DELETE_PROJECT_BTN', {
                    project_id: project_id,
                  });
                  setConfirmDeleteProject(true);
                }}
                data-cy="emptyState_deleteProjectButton"
              >
                <HiOutlineTrash className="w-5 h-5" />
                <span>Delete Project</span>
              </button>
            </div>
          </div>
        );
      }

      return (
        <div className="flex flex-col">
          <div className="justify-self-center text-base leading-7 font-normal text-gray-500 pb-4">
            You don&apos;t have any datasets yet. Start a{' '}
            <Link
              className="link"
              to={project_id ? `/project/${project_id}/search` : '/project'}
            >
              search
            </Link>{' '}
            to build one.
          </div>
          <div className="grid grid-rows-1 grid-cols-2 gap-x-4">
            <div className="border border-transparent rounded-md shadow-sm border-gray-300 z-10 overflow-hidden">
              <EmbedVideo src="https://www.loom.com/embed/6076d589bf4949369bbd9f6246137830?hide_share=true&hide_owner=true&hideEmbedTopBar=true" />
              <div className="ml-2 py-2">
                <a
                  className="link"
                  href="https://www.loom.com/share/6076d589bf4949369bbd9f6246137830"
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackEvent('CLICK_DEMO_HOW_TO_SEARCH_BTN')}
                >
                  How to search &rarr;
                </a>
              </div>
            </div>
            <div className="border border-transparent rounded-md shadow-sm border-gray-300 z-10 overflow-hidden">
              <EmbedVideo src="https://www.loom.com/embed/1a183ebc11c247eb88d78036211ad9d1?hide_share=true&hide_owner=true&hideEmbedTopBar=true" />
              <div className="ml-2 py-2">
                <a
                  className="link"
                  href="https://www.loom.com/share/1a183ebc11c247eb88d78036211ad9d1"
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackEvent('CLICK_DEMO_HOW_TO_ORDER_BTN')}
                >
                  How to order data &rarr;
                </a>
              </div>
            </div>
          </div>
          <div className="mt-4">
            <a
              className="link"
              href="https://www.segmed.ai/resources/help"
              target="_blank"
              rel="noreferrer"
              onClick={() => trackEvent('CLICK_DEMO_AND_GUIDES_BTN')}
            >
              Demo and Guides: Getting Started &rarr;
            </a>
          </div>
        </div>
      );
    }

    // Datasets are already filtered by project_id if we have one, so just apply search and status filters
    const filteredDatasets = _.filter(
      datasets,
      dataset =>
        dataset.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
        (statusFilter === 'All' ||
          GroupedStatuses[statusFilter].includes(dataset.status))
    );

    if (filteredDatasets.length === 0) {
      return (
        <div className="text-center text-gray-500">
          No datasets match your search and filter criteria
        </div>
      );
    }

    return (
      <div className="min-w-full space-y-4">
        {_.map(filteredDatasets, (dataset, i) => (
          <DatasetCard
            key={i}
            dataset={dataset}
            action={actionType => {
              switch (actionType) {
                case 'edit':
                case 'archive':
                case 'delete':
                  openModal(actionType, dataset);
                  break;
                case 'request':
                  // do nothing
                  break;
              }
            }}
          />
        ))}
      </div>
    );
  };

  const renderEditModal = (dataset: Dataset) => {
    return (
      <div>
        <div className="text-lg mb-3">Rename Dataset</div>
        <div className="mb-3">
          <label htmlFor="name" className="input-label">
            New name
          </label>
          <input
            id="name"
            type="text"
            className="text-input w-full mt-2"
            value={dataset.name}
            autoComplete="off"
            onChange={e => {
              dataset.name = e.target.value;
              modalDatasetChange(_.cloneDeep(dataset));
            }}
          />
        </div>
        <div className="flex flex-row-reverse">
          <button
            className="btn btn-primary"
            onClick={() => {
              editDatasetMut.mutate({
                datasetId: dataset!.id,
                name: dataset!.name,
              });
            }}
            disabled={editDatasetMut.isLoading}
          >
            Save
          </button>
        </div>
      </div>
    );
  };

  const renderDeleteModal = (dataset: Dataset) => {
    return (
      <div>
        <div className="text-lg mb-3">Delete Dataset</div>
        <div className="text-sm text-gray-500 mb-3">
          All reports and tags saved to this dataset will be lost.
        </div>
        <div className="flex flex-row-reverse">
          <button
            className="btn btn-danger"
            data-cy="confirmDeleteDataset"
            onClick={() => {
              deleteDatasetMut.mutate({datasetId: dataset!.id});
            }}
            disabled={deleteDatasetMut.isLoading}
          >
            Delete
          </button>
        </div>
      </div>
    );
  };

  const renderArchiveModal = (dataset: Dataset) => {
    return (
      <div>
        <div className="text-lg mb-3">Archive Dataset</div>
        <div className="text-sm text-gray-500 mb-3">
          This dataset will be archived and will be no longer accessible thru
          this page, do you want to continue?
        </div>
        <div className="flex flex-row-reverse">
          <button
            className="btn btn-danger"
            onClick={() => {
              archiveDatasetMut.mutate({datasetId: dataset!.id});
            }}
            disabled={archiveDatasetMut.isLoading}
          >
            Archive
          </button>
        </div>
      </div>
    );
  };

  // Show loading state while fetching project data
  if (project_id && isProjectsLoading) {
    return (
      <ApplicationShell bgcolor="bgcolor">
        <Loading text="Loading project..." />
      </ApplicationShell>
    );
  }

  // Show error if project not found
  if (project_id && !project) {
    return (
      <ApplicationShell bgcolor="bgcolor">
        <div className="p-6">
          <div className="text-center text-gray-500 py-6">
            Project not found
          </div>
        </div>
      </ApplicationShell>
    );
  }

  return (
    <>
      {loading && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="rounded-md bg-black">
            <Loading className="block p-2 text-white" />
          </div>
        </div>
      )}
      <Helmet>
        <title>
          Segmed Openda - {project_id ? project?.name || 'Project' : 'Datasets'}
        </title>
      </Helmet>

      <ApplicationShell bgcolor="bgcolor" contained={project_id ? false : true}>
        {project_id && (
          <div className="p-6">
            <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
              <Breadcrumbs
                links={[
                  {name: 'Projects', href: '/project'},
                  {name: project?.name || '', current: true},
                ]}
              />
            </div>

            {/* Project Name */}
            <div className="mb-6">
              <h1 className="text-2xl font-semibold text-gray-800">
                {project?.name}
              </h1>
              {isLocked && (
                <span className="text-sm text-gray-500 ml-2">(Closed)</span>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-4 mb-6">
              <button
                className="btn btn-white py-1.5 flex items-center gap-2"
                onClick={() => {
                  trackEvent('CLICK_SEARCH_BTN', {project_id: project_id});
                  onSearchClick();
                }}
                disabled={isLocked}
              >
                <HiOutlineSearch className="w-5 h-5" />
                <span>Search Page</span>
              </button>
              {isInternalUser && (
                <button
                  className="btn btn-white py-1.5 flex items-center gap-2"
                  onClick={() => {
                    trackEvent('CLICK_TERMINAL_SEARCH_BTN', {
                      project_id: project_id,
                    });
                    onTerminalSearch();
                  }}
                  disabled={isLocked}
                >
                  <HiOutlineTerminal className="w-5 h-5" />
                  <span>Terminal Search</span>
                </button>
              )}
            </div>
          </div>
        )}

        <div className={project_id ? 'px-6' : ''}>
          <div className="mb-5 flex justify-between items-center">
            <h1 className="text-3xl mr-4">
              {project_id ? 'Datasets' : 'Your Datasets'}
            </h1>
            <div className="flex items-center gap-x-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search datasets..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="text-input pl-10 pr-4"
                />
                <SearchIcon className="text-gray-400 w-6 h-6 absolute left-2 top-1/2 transform -translate-y-1/2" />
              </div>
              <Select
                value={statusFilter}
                onSelect={(
                  option: SelectOption<keyof typeof GroupedStatuses>
                ) => setStatusFilter(option.value)}
                text={
                  statusFilter === 'All'
                    ? 'All Statuses'
                    : _.startCase(statusFilter)
                }
                options={[
                  {value: 'All', label: 'All Statuses'},
                  ...Object.keys(GroupedStatuses)
                    .filter(
                      group =>
                        group !== 'All' &&
                        datasets?.some(dataset =>
                          GroupedStatuses[
                            group as keyof typeof GroupedStatuses
                          ].includes(dataset.status)
                        )
                    )
                    .map(group => ({
                      value: group as keyof typeof GroupedStatuses,
                      label: _.startCase(group),
                    })),
                ]}
                contained
                selectTheme="white"
              />
            </div>
            <button
              className="btn btn-white py-1.5 flex items-center gap-2"
              onClick={() => {
                trackEvent('CLICK_CREATE_DATASET_BTN', {
                  project_id: project_id,
                });
                if (project_id && !isLocked && isProjectOwner) {
                  setProjectId(project_id);
                }
                openModal('create');
              }}
              disabled={project_id ? isLocked || !isProjectOwner : false}
              data-cy="createDatasetButton"
            >
              <HiOutlinePlus className="w-5 h-5" />
              <span>New Dataset</span>
            </button>
          </div>
          {!loading && <div>{renderDatasets()}</div>}
        </div>
      </ApplicationShell>

      <Modal
        isOpen={modalOpen}
        onRequestClose={() => closeModal()}
        className="w-96 max-w-lg"
        hideExitButton={_.includes(['requestConfirmed'], modalType)}
      >
        {modalType === 'edit' && renderEditModal(modalDataset!)}
        {modalType === 'delete' && renderDeleteModal(modalDataset!)}
        {modalType === 'archive' && renderArchiveModal(modalDataset!)}
        {modalType === 'create' && project_id && (
          <CreateDatasetForm
            allowCSVUpload
            closeModal={closeModal}
            setLoading={setLoading}
            projectID={project_id}
            created={() => {
              if (project_id) {
                queryClient.invalidateQueries(['projectsByOwner']);
              } else {
                queryClient.invalidateQueries(['dataset']);
              }
            }}
          />
        )}
      </Modal>

      <Modal
        isOpen={confirmDeleteProject}
        onRequestClose={() => setConfirmDeleteProject(false)}
        className="w-96 max-w-lg"
      >
        <div className="text-lg mb-3">Delete Project</div>
        <div className="text-sm text-gray-500 mb-3">
          Are you sure you want to delete this project? This action cannot be
          undone.
        </div>
        <div className="flex flex-row-reverse space-x-2 space-x-reverse">
          <button
            className="btn btn-danger"
            onClick={() => {
              handleDeleteProject();
              setConfirmDeleteProject(false);
            }}
            disabled={deleteProjectMutation.isLoading}
          >
            Delete
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => setConfirmDeleteProject(false)}
          >
            Cancel
          </button>
        </div>
      </Modal>
    </>
  );
};
